import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class SliderComponent extends Component {
  #raf = 0;
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;
    this.count = this.querySelector('[data-slider-count]');
    this.prev = [...this.querySelectorAll('[data-slider-prev]')];
    this.next = [...this.querySelectorAll('[data-slider-next]')];
    this.prev.forEach((b) => b.addEventListener('click', () => this.#page(-1)));
    this.next.forEach((b) => b.addEventListener('click', () => this.#page(1)));
    this.track.addEventListener('scroll', () => this.#request(), { passive: true });
    this.#observer = new ResizeObserver(() => this.#request());
    this.#observer.observe(this.track);
    this.#accessoDaTastiera();
    this.#trascinamentoMouse();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  // sotto i 768px le frecce sono nascoste: la traccia resta l unico appiglio da tastiera
  #accessoDaTastiera() {
    const frecceVisibili = this.prev.some((b) => b.offsetParent !== null);
    if (frecceVisibili) {
      this.track.removeAttribute('tabindex');
      this.track.removeAttribute('role');
      return;
    }
    this.track.tabIndex = 0;
    this.track.setAttribute('role', 'group');
    const titolo = this.querySelector('.slider__heading, [data-slider-heading], h2, h3');
    if (titolo && !this.track.hasAttribute('aria-labelledby')) {
      if (!titolo.id) titolo.id = `slider-titolo-${Math.round(performance.now())}-${this.#cells().length}`;
      this.track.setAttribute('aria-labelledby', titolo.id);
    }
  }

  // Col mouse la pista si trascina come col dito. Lo snap si spegne durante il gesto (se no ogni
  // pixel verrebbe riagganciato alla card) e torna al rilascio, che aggancia la card piu vicina.
  // Se la mano si e' spostata il click che segue viene mangiato: si voleva scorrere, non aprire la card.
  #trascinamentoMouse() {
    const track = this.track;
    let inizio = null;
    let mosso = false;

    track.addEventListener('pointerdown', (evento) => {
      if (evento.pointerType !== 'mouse' || evento.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth) return;
      inizio = { x: evento.clientX, scroll: track.scrollLeft };
      mosso = false;
    });

    track.addEventListener('pointermove', (evento) => {
      if (!inizio) return;
      const dx = evento.clientX - inizio.x;
      if (!mosso) {
        if (Math.abs(dx) < 5) return;
        mosso = true;
        track.setPointerCapture(evento.pointerId);
        track.classList.add('is-dragging');
      }
      track.scrollLeft = inizio.scroll - dx;
    });

    const fine = (evento) => {
      if (!inizio) return;
      inizio = null;
      if (!mosso) return;
      if (track.hasPointerCapture(evento.pointerId)) track.releasePointerCapture(evento.pointerId);
      // snap-type torna prima di smooth: il browser riaggancia la card piu vicina con l animazione
      const posizione = track.scrollLeft;
      track.classList.remove('is-dragging');
      track.scrollLeft = posizione;
    };
    // Firefox non conosce -webkit-user-drag: senza questo il gesto trascinerebbe la foto
    track.addEventListener('dragstart', (evento) => evento.preventDefault());
    track.addEventListener('pointerup', fine);
    track.addEventListener('pointercancel', fine);

    track.addEventListener(
      'click',
      (evento) => {
        if (!mosso) return;
        mosso = false;
        evento.preventDefault();
        evento.stopPropagation();
      },
      true
    );
  }

  #cells() {
    return [...this.track.children].filter((cell) => !cell.hidden);
  }

  #step(cells) {
    if (cells.length > 1) return cells[1].offsetLeft - cells[0].offsetLeft;
    return cells[0]?.offsetWidth || 0;
  }

  #page(dir) {
    const step = this.#step(this.#cells());
    if (!step) return;
    this.track.scrollBy({
      left: step * dir,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #request() {
    if (!this.#raf) this.#raf = requestAnimationFrame(() => this.#update());
  }

  #update() {
    this.#raf = 0;
    this.#accessoDaTastiera();
    const cells = this.#cells();
    const step = this.#step(cells);

    const scorso = Math.abs(this.track.scrollLeft);
    const scorribile = this.track.scrollWidth - this.track.clientWidth;
    const tolleranza = 8;
    const spegni = (bottoni, spento, gemelli) => {
      for (const b of bottoni) {
        if (spento && document.activeElement === b) gemelli[0]?.focus();
        b.disabled = spento;
      }
    };
    spegni(this.prev, scorso <= tolleranza, this.next);
    spegni(this.next, scorso >= scorribile - tolleranza, this.prev);

    if (!this.count || !step) return;
    const gap = step - (cells[0]?.offsetWidth || step);
    const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
    const current = Math.min(cells.length, Math.round(scorso / step) + perView);
    this.count.textContent = current + '/' + cells.length;
  }
}

if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);
